import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import CommaSeparatedList from '@/components/molecules/comma-separated-list/comma-separated-list';
import styles from './search-bar-section.module.scss';

export interface SearchBarSectionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether to hide the arrow */
  hideArrow?: boolean;
  /** Indicates whether the field is active */
  isActive?: boolean;
  /** Indicates whether the field is in a loading state */
  isLoading?: boolean;
  /** Indicates whether the field is notified that has changes */
  isNotified?: boolean;
  /** The text that is shown above the field as a label */
  label: string;
  /** An event that is triggered on click */
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** The placeholder of the field */
  placeholder?: string;
  /** The value of the field */
  value?: string[] | string;
}

const SearchBarSection = ({
  className,
  disabled = false,
  hideArrow = false,
  isActive = false,
  isLoading = false,
  isNotified = false,
  label,
  placeholder,
  value = [],
  onClick,
  ...rest
}: SearchBarSectionProps) => {
  return (
    <button
      {...rest}
      // Stays focusable when disabled, so focus isn't lost when it gets disabled
      // while focused, and the click is blocked in onClick instead
      aria-disabled={disabled || undefined}
      aria-expanded={isActive}
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.isDisabled]: disabled,
        [styles.isNotified]: isNotified,
      })}
      onClick={event => {
        if (disabled) return;

        onClick?.(event);
      }}
      type="button"
    >
      <div className={styles.content}>
        {label && (
          <Typography
            className={styles.label}
            tag="span"
            tagStyle="labelMedium"
          >
            {label}
          </Typography>
        )}

        {isLoading ? (
          <SkeletonLoader className={styles.skeleton} width="60%" />
        ) : (
          <React.Fragment>
            <div className={styles.valueContainer}>
              {isNotified && (
                <div className={styles.iconContainer}>
                  <Icon name="Warning" size={16} />
                </div>
              )}

              {placeholder && (!value.length || value === undefined) && (
                <Typography
                  className={styles.placeholder}
                  tagStyle="bodyMedium"
                >
                  {placeholder}
                </Typography>
              )}

              {typeof value === 'string' && (
                <Typography className={styles.value} tagStyle="bodyMedium">
                  {value}
                </Typography>
              )}

              {Array.isArray(value) && value.length > 0 && (
                <CommaSeparatedList list={value} />
              )}
            </div>
          </React.Fragment>
        )}
      </div>

      {!hideArrow && (
        <Icon
          aria-hidden="true"
          className={styles.icon}
          name="ChevronRight"
          size={14}
        />
      )}
    </button>
  );
};

SearchBarSection.displayName = 'SearchBarSection';

export default SearchBarSection;
